import { DB_NAME, DB_VERSION, STORES } from './schema';

export function openNativeDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      return reject(new Error('IndexedDB is not available in this environment'));
    }

    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      const oldVersion = (event as IDBVersionChangeEvent).oldVersion;

      const storesWithId = [
        STORES.subjects,
        STORES.syllabusUnits,
        STORES.chapters,
        STORES.topics,
        STORES.pastPapers,
        STORES.paperTopicLinks,
        STORES.studySessions,
        STORES.tasks,
        STORES.revisionEvents,
        STORES.exams,
        STORES.goals,
        STORES.studentProfile,
        STORES.changeJournal,
        STORES.conflictRecords,
      ];

      for (const s of storesWithId) {
        if (!db.objectStoreNames.contains(s)) {
          const store = db.createObjectStore(s, { keyPath: 'id' });
          store.createIndex('updatedAt', 'updatedAt', { unique: false });
          store.createIndex('deletedAt', 'deletedAt', { unique: false });
        }
      }

      const pastPapers = req.transaction?.objectStore(STORES.pastPapers);
      if (oldVersion < 2 && pastPapers && !pastPapers.indexNames.contains('subjectYearType')) {
        pastPapers.createIndex('subjectYearType', ['subjectId', 'year', 'paperType'], { unique: true });
      }

      if (oldVersion < 4 && pastPapers?.indexNames.contains('subjectYearType')) {
        pastPapers.deleteIndex('subjectYearType');
      }

      if (oldVersion < 2) {
        const paperCursor = pastPapers?.openCursor();
        if (paperCursor) paperCursor.onsuccess = () => {
          const cursor = paperCursor.result;
          if (!cursor) return;
          const record = cursor.value;
          if (record.originDeviceId === 'system-seed') {
            cursor.update({
              ...record,
              status: 'NOT_STARTED',
              difficulty: null,
              isRevisionRequired: false,
              score: null,
            });
          }
          cursor.continue();
        };

        const topicCursor = req.transaction?.objectStore(STORES.topics).openCursor();
        if (topicCursor) topicCursor.onsuccess = () => {
          const cursor = topicCursor.result;
          if (!cursor) return;
          const record = cursor.value;
          if (record.originDeviceId === 'system-seed') {
            cursor.update({ ...record, status: 'NOT_STARTED', masteryScore: 0, notes: '' });
          }
          cursor.continue();
        };
      }

      if (!db.objectStoreNames.contains(STORES.syncCheckpoints)) {
        db.createObjectStore(STORES.syncCheckpoints, { keyPath: 'remoteDeviceId' });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('Database upgrade is blocked by another open app tab. Close other tabs and reload.'));
  });
}
